import { getAuthRedirectUri, paramsToQuery } from '@/base/MaUtils'
import { makeRequest } from '@/base/MaRequestPooler'
import { AUTH_PLATFORM } from '@/base/constants/Auth'

export function socialLoginRequest(type, code, platform = AUTH_PLATFORM.MA) {
  return makeRequest(`/authentication/sign-in/${type}`, {
    method: 'POST',
    data: {
      code,
      redirectUri: getAuthRedirectUri(type),
      platform,
    },
  }, { disableAuthCheck: true })
}

export function appleLoginRequest(data) {
  return makeRequest(`/authentication/sign-in/apple`, {
    method: 'POST',
    data,
  }, { disableAuthCheck: true })
}

export function loginWithEmail({ username, password, userResponse, platform = AUTH_PLATFORM.MA }) {
  return makeRequest('/authentication/sign-in', {
    method: 'POST',
    data: {
      username,
      password,
      userResponse,
      platform,
    },
  }, { disableAuthCheck: true })
}

export function register({ username, platform = AUTH_PLATFORM.MA, isEnterprise = false, ...form }) {
  return makeRequest('/authentication/sign-up', {
    method: 'POST',
    data: {
      platform,
      isEnterprise,
      email: username,
      ...form,
    },
  }, { disableAuthCheck: true })
}

export function resendRegisterVerificationEmail(userId) {
  return makeRequest(`/authentication/user/${userId}/resend`, { method: 'POST' }, { disableAuthCheck: true })
}

/**
 * Sends forgot password code email to client
 * @param username //email
 * @param userResponse // re-captcha response
 * @returns {Promise} //empty
 */
export function requestPasswordReset({ username, userResponse, platform = AUTH_PLATFORM.MA }) {
  return makeRequest(`/password-reset/send/v2`, {
    method: 'POST',
    data: {
      username,
      userResponse,
      platform,
    },
  }, { disableAuthCheck: true })
}

/**
 * Returns token and userId needed for creating a new password
 * @param username //email
 * @param resetCode // 6 digits number
 * @returns {Promise} //{ token, userId }
 */
export function requestPasswordResetVerify({ username, resetCode }) {
  return makeRequest(`/password-reset/verify`, {
    method: 'POST',
    data: {
      username,
      resetCode,
    },
  }, { disableAuthCheck: true })
}

export function getPasswordResetInfoByToken(token) {
  return makeRequest(`/password-reset/${token}`, {}, { disableAuthCheck: true })
}

export function resetPassword(token, userId, password, passwordConfirm) {
  return makeRequest('/password-reset/reset', {
    method: 'POST',
    data: { token, userId, password, passwordConfirm },
  }, { disableAuthCheck: true })
}

export function verifyUser(token, platform = AUTH_PLATFORM.MA) {
  return makeRequest(`/authentication/sign-up/verify/${token}?platform=${platform}`, { method: 'POST' }, { disableAuthCheck: true })
}

export function getInvitation(token) {
  return makeRequest(`/authentication/team-member/invitation/?${paramsToQuery({ token })}`, {}, { disableAuthCheck: true })
}

export function completeInvitation(data) {
  return makeRequest('/authentication/team-member/invitation/complete', {
    method: 'POST',
    data,
  }, { disableAuthCheck: true })
}

/**
 * Start SSO
 * @param username //email
 * @returns {Promise} //SSO Discovery
 */
export function startSSO(data) {
  return makeRequest(`/authentication/sso/sign-in`, {
    method: 'POST',
    data,
  }, { disableAuthCheck: true })
}

/**
 * Login to MA with SSO accessToken & idToken
 * @param accessuserNameToken //email
 * @param accessToken //JWT
 * @param idToken //JWT
 * @returns {Promise} //userAuth
 */
export function verifySSO({ userName, accessToken, idToken, platform = AUTH_PLATFORM.MA }) {
  return makeRequest(`/authentication/sso/verify`, {
    method: 'POST',
    data: {
      userName,
      accessToken,
      idToken,
      platform,
    },
  }, { disableAuthCheck: true })
}

/**
 * Logs out the user by invalidating their refresh token.
 *
 * @param {string} refreshToken The user's refresh token to invalidate.
 * @returns {Promise<void>} A promise that resolves upon successful logout.
 */
export function logout(refreshToken) {
  return makeRequest(`/authentication/sign-out`, {
    method: 'POST',
    data: {
      refreshToken,
    },
  })
}
